/**
 * Município e estado de um local das listas de negócios locais. As listas guardam só
 * "lat,lng|nome curto" (o nome pode ser um bairro, como "Centro"), então a Home procura:
 *   1. um município com o mesmo nome a até 80 km (acerta mesmo nos municípios grandes, em que o
 *      ponto da tabela fica longe da cidade);
 *   2. senão, o município com o ponto mais próximo, a até 60 km.
 * Mais longe que isso, o local fica fora do Brasil.
 *
 * A tabela (municipalities.json) sai de scripts/build-municipalities.mjs e só é baixada quando
 * a Home tem listas de negócios locais.
 */
import { CAPITAL_CODES, stateOf } from './brazilStates';

/** [código do IBGE, nome, latitude, longitude] */
export type MunicipalityRow = [number, string, number, number];

export interface Municipality {
  code: number;
  name: string;
  uf: string;
  lat: number;
  lng: number;
  capital: boolean;
}

export type FindMunicipality = (lat: number, lng: number, name: string) => Municipality | null;

const NAME_RADIUS_KM = 80;
const NEAREST_RADIUS_KM = 60;
/** Células de 1° para a busca do mais próximo não percorrer a tabela toda. */
const CELL = 1;

export function distanceKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const rad = (d: number): number => (d * Math.PI) / 180;
  const h =
    Math.sin(rad(b.lat - a.lat) / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lng - a.lng) / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h));
}

/** "São José dos Pinhais" e "sao jose dos pinhais" ficam iguais. */
export function normalizeName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const cellKey = (lat: number, lng: number): string =>
  `${Math.floor(lat / CELL)}:${Math.floor(lng / CELL)}`;

export function createFinder(rows: MunicipalityRow[]): FindMunicipality {
  const byName = new Map<string, Municipality[]>();
  const byCell = new Map<string, Municipality[]>();
  for (const [code, name, lat, lng] of rows) {
    const state = stateOf(code);
    if (!state) continue;
    const m: Municipality = { code, name, uf: state.uf, lat, lng, capital: CAPITAL_CODES.has(code) };
    const key = normalizeName(name);
    byName.set(key, [...(byName.get(key) ?? []), m]);
    const cell = cellKey(lat, lng);
    byCell.set(cell, [...(byCell.get(cell) ?? []), m]);
  }

  return (lat, lng, name) => {
    const point = { lat, lng };
    let best: Municipality | null = null;
    let bestKm = Infinity;
    for (const m of byName.get(normalizeName(name)) ?? []) {
      const km = distanceKm(point, m);
      if (km <= NAME_RADIUS_KM && km < bestKm) {
        best = m;
        bestKm = km;
      }
    }
    if (best) return best;

    // Células vizinhas (1° ≈ 111 km cobre o raio de 60 km).
    const row = Math.floor(lat / CELL);
    const col = Math.floor(lng / CELL);
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        for (const m of byCell.get(`${row + dr}:${col + dc}`) ?? []) {
          const km = distanceKm(point, m);
          if (km <= NEAREST_RADIUS_KM && km < bestKm) {
            best = m;
            bestKm = km;
          }
        }
      }
    }
    return best;
  };
}

let finder: Promise<FindMunicipality> | null = null;

/** Baixa a tabela uma vez (fica num pedaço separado do código) e monta a busca. */
export function loadMunicipalityFinder(): Promise<FindMunicipality> {
  finder ??= import('./municipalities.json')
    .then((module) => createFinder(module.default as MunicipalityRow[]))
    .catch((error: unknown) => {
      finder = null;
      throw error;
    });
  return finder;
}
